/**
 * formulaEngine.js — safe Excel-like formula evaluator (Feb 2026 · sprint 375).
 *
 * WHY custom vs a library?
 *   • HyperFormula (Excel-parity but 600KB, GPL+commercial license mix).
 *   • hot-formula-parser is MIT but doesn't handle range refs cleanly and
 *     hasn't been maintained in years.
 *   • The requested V1 subset (arithmetic + SUM/AVERAGE/MIN/MAX/COUNT/COUNTA/
 *     SUMIF/COUNTIF/AVERAGEIF/IF/AND/OR/ROUND/ROUNDUP/ROUNDDOWN) is small
 *     enough that a hand-rolled recursive-descent parser is safer and
 *     bounded — NO javascript eval, NO code injection surface.
 *
 * Architecture:
 *   evaluate(formula, cellLookup, opts) — main entry
 *     • formula: string like "=SUM(A1:A5)+B7*100"
 *     • cellLookup(col, row) → { raw: number|string|null, type: 'n'|'s'|'d'|'b' }
 *     • opts.visitedRefs: set for circular detection.
 *   Returns: { value, error, kind }
 *     • kind = 'number' | 'string' | 'boolean' | 'error'
 *     • error is one of #DIV/0!, #VALUE!, #REF!, #NAME?, #CIRC!
 *
 * Cell refs use standard A1 notation. Column A=1, B=2, ..., Z=26, AA=27, ...
 */

const ERR = {
  DIV0: '#DIV/0!',
  VALUE: '#VALUE!',
  REF: '#REF!',
  NAME: '#NAME?',
  CIRC: '#CIRC!',
  NUM: '#NUM!',
};

// ── A1 helpers ────────────────────────────────────────────────────────
export function colLettersToIndex(letters) {
  let n = 0;
  for (const ch of letters.toUpperCase()) {
    n = n * 26 + (ch.charCodeAt(0) - 64);
  }
  return n;
}
export function colIndexToLetters(idx) {
  let s = '';
  let n = idx;
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s || 'A';
}
function parseA1(ref) {
  const m = /^([A-Za-z]+)(\d+)$/.exec(ref);
  if (!m) return null;
  return { col: colLettersToIndex(m[1]), row: parseInt(m[2], 10), ref: ref.toUpperCase() };
}

// ── Tokenizer ─────────────────────────────────────────────────────────
function tokenize(src) {
  const tokens = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (c === ' ' || c === '\t' || c === '\n') { i++; continue; }
    if (c === '<' || c === '>' || c === '=') {
      // Comparison operators: <, >, <=, >=, =, <>
      let two = src[i + 1];
      let op = c;
      if ((c === '<' && (two === '=' || two === '>')) ||
          (c === '>' && two === '=')) { op = c + two; i += 2; }
      else { i += 1; }
      tokens.push({ t: 'cmp', v: op });
      continue;
    }
    if ('+-*/^%(),'.includes(c)) { tokens.push({ t: 'op', v: c }); i++; continue; }
    if (c === '"') {
      let j = i + 1; let out = '';
      while (j < src.length && src[j] !== '"') { out += src[j]; j++; }
      if (src[j] !== '"') throw new SyntaxError(ERR.VALUE);
      tokens.push({ t: 'str', v: out }); i = j + 1; continue;
    }
    if (/[0-9.]/.test(c)) {
      let j = i;
      while (j < src.length && /[0-9.]/.test(src[j])) j++;
      const n = Number(src.slice(i, j));
      if (!Number.isFinite(n)) throw new SyntaxError(ERR.NUM);
      tokens.push({ t: 'num', v: n }); i = j; continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      // Letters/underscore only — digits are handled separately for A1 refs.
      let j = i;
      while (j < src.length && /[A-Za-z_]/.test(src[j])) j++;
      const name = src.slice(i, j).toUpperCase();
      // Peek for '(' to distinguish function vs cell ref
      let k = j;
      while (k < src.length && src[k] === ' ') k++;
      if (src[k] === '(') {
        tokens.push({ t: 'fn', v: name });
        i = j; continue;
      }
      // Try as A1 ref (letters must be followed by digits)
      let letters = name;
      let m = j;
      while (m < src.length && /[0-9]/.test(src[m])) m++;
      if (m > j) {
        const rowStr = src.slice(j, m);
        const digits = parseInt(rowStr, 10);
        if (Number.isFinite(digits)) {
          // Optional range: A1:B5
          let n = m;
          if (src[n] === ':') {
            n++;
            let p = n;
            while (p < src.length && /[A-Za-z]/.test(src[p])) p++;
            let q = p;
            while (q < src.length && /[0-9]/.test(src[q])) q++;
            if (q > p) {
              const l2 = src.slice(n, p).toUpperCase();
              const d2 = parseInt(src.slice(p, q), 10);
              tokens.push({ t: 'range', a: { col: colLettersToIndex(letters), row: digits },
                                            b: { col: colLettersToIndex(l2),     row: d2 } });
              i = q; continue;
            }
          }
          tokens.push({ t: 'ref', col: colLettersToIndex(letters), row: digits,
                                    ref: `${letters}${digits}` });
          i = m; continue;
        }
      }
      // Bare identifier: TRUE / FALSE
      if (name === 'TRUE') { tokens.push({ t: 'bool', v: true }); i = j; continue; }
      if (name === 'FALSE') { tokens.push({ t: 'bool', v: false }); i = j; continue; }
      throw new SyntaxError(ERR.NAME);
    }
    throw new SyntaxError(ERR.VALUE);
  }
  return tokens;
}

// ── Recursive-descent parser + evaluator ──────────────────────────────
class Parser {
  constructor(tokens, ctx) { this.tokens = tokens; this.p = 0; this.ctx = ctx; }
  peek() { return this.tokens[this.p]; }
  eat(t, v) {
    const tok = this.tokens[this.p];
    if (!tok || tok.t !== t || (v !== undefined && tok.v !== v)) {
      throw new SyntaxError(ERR.VALUE);
    }
    this.p++; return tok;
  }
  parseExpr() {
    // Comparison operators have lower precedence than arithmetic.
    let left = this.parseSum();
    while (this.peek()?.t === 'cmp') {
      const op = this.tokens[this.p++].v;
      const right = this.parseSum();
      left = { kind: 'cmp', op, a: left, b: right };
    }
    return left;
  }
  parseSum() {
    let left = this.parseTerm();
    while (this.peek()?.t === 'op' && (this.peek().v === '+' || this.peek().v === '-')) {
      const op = this.tokens[this.p++].v;
      const right = this.parseTerm();
      left = { kind: 'bin', op, a: left, b: right };
    }
    return left;
  }
  parseTerm() {
    let left = this.parseFactor();
    while (this.peek()?.t === 'op' &&
           (this.peek().v === '*' || this.peek().v === '/' || this.peek().v === '%')) {
      const op = this.tokens[this.p++].v;
      const right = this.parseFactor();
      left = { kind: 'bin', op, a: left, b: right };
    }
    return left;
  }
  parseFactor() {
    let left = this.parseUnary();
    while (this.peek()?.t === 'op' && this.peek().v === '^') {
      this.p++;
      const right = this.parseUnary();
      left = { kind: 'bin', op: '^', a: left, b: right };
    }
    return left;
  }
  parseUnary() {
    const t = this.peek();
    if (t?.t === 'op' && (t.v === '+' || t.v === '-')) {
      this.p++;
      return { kind: 'un', op: t.v, a: this.parseUnary() };
    }
    return this.parsePrimary();
  }
  parsePrimary() {
    const t = this.peek();
    if (!t) throw new SyntaxError(ERR.VALUE);
    if (t.t === 'num')  { this.p++; return { kind: 'num', v: t.v }; }
    if (t.t === 'str')  { this.p++; return { kind: 'str', v: t.v }; }
    if (t.t === 'bool') { this.p++; return { kind: 'bool', v: t.v }; }
    if (t.t === 'ref')  { this.p++; return { kind: 'ref', col: t.col, row: t.row, ref: t.ref }; }
    if (t.t === 'range'){ this.p++; return { kind: 'range', a: t.a, b: t.b }; }
    if (t.t === 'op' && t.v === '(') {
      this.p++;
      const e = this.parseExpr();
      this.eat('op', ')');
      return e;
    }
    if (t.t === 'fn') {
      const fn = t.v; this.p++;
      this.eat('op', '(');
      const args = [];
      if (this.peek()?.v !== ')') {
        args.push(this.parseExpr());
        while (this.peek()?.t === 'op' && this.peek().v === ',') {
          this.p++;
          args.push(this.parseExpr());
        }
      }
      this.eat('op', ')');
      return { kind: 'fn', fn, args };
    }
    throw new SyntaxError(ERR.VALUE);
  }
}

// ── Value coercion helpers ────────────────────────────────────────────
function toNum(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v == null || v === '') return 0;
  const n = Number(String(v).replace(/[,₹%\s]/g, ''));
  return Number.isFinite(n) ? n : NaN;
}
function toBool(v) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  return !!(v && v !== 'FALSE' && v !== 'false');
}
function flattenRange(a, b, resolve) {
  const c1 = Math.min(a.col, b.col), c2 = Math.max(a.col, b.col);
  const r1 = Math.min(a.row, b.row), r2 = Math.max(a.row, b.row);
  const out = [];
  for (let r = r1; r <= r2; r++)
    for (let c = c1; c <= c2; c++)
      out.push(resolve(c, r));
  return out;
}

// ── SUMIF/COUNTIF criteria matcher ────────────────────────────────────
function matchCriteria(cellValue, criteria) {
  if (criteria == null) return false;
  const cStr = String(criteria).trim();
  const opMatch = /^(<=|>=|<>|<|>|=)(.+)$/.exec(cStr);
  if (opMatch) {
    const [, op, rhsRaw] = opMatch;
    const rhs = isNaN(Number(rhsRaw)) ? rhsRaw : Number(rhsRaw);
    const lhs = typeof cellValue === 'number' ? cellValue : (isNaN(Number(cellValue)) ? cellValue : Number(cellValue));
    switch (op) {
      case '<':  return lhs <  rhs;
      case '<=': return lhs <= rhs;
      case '>':  return lhs >  rhs;
      case '>=': return lhs >= rhs;
      case '=':  return String(lhs) === String(rhs);
      case '<>': return String(lhs) !== String(rhs);
      default: return false;
    }
  }
  return String(cellValue ?? '').toLowerCase() === cStr.toLowerCase();
}

// ── AST evaluator ─────────────────────────────────────────────────────
function evalNode(node, ctx) {
  const resolveRef = (col, row) => {
    const key = `${col}:${row}`;
    if (ctx.visited.has(key)) throw new Error(ERR.CIRC);
    ctx.visited.add(key);
    try {
      const v = ctx.cellLookup(col, row);
      return v;
    } finally { ctx.visited.delete(key); }
  };
  // Returns the RAW value (number OR string) so criteria matchers work.
  // For arithmetic sites we run `toNum` on the return value.
  const resolveRaw = (col, row) => {
    const cell = resolveRef(col, row);
    if (cell == null) return null;
    if (typeof cell === 'object') return cell.raw ?? null;
    return cell;
  };
  const resolveNumeric = (col, row) => {
    const raw = resolveRaw(col, row);
    if (raw == null || raw === '') return 0;
    const n = toNum(raw);
    return Number.isFinite(n) ? n : NaN;
  };

  switch (node.kind) {
    case 'num':  return node.v;
    case 'str':  return node.v;
    case 'bool': return node.v;
    case 'ref':  return resolveRaw(node.col, node.row);
    case 'range':
      return flattenRange(node.a, node.b, (c, r) => resolveRaw(c, r));
    case 'un': {
      const a = evalNode(node.a, ctx);
      return node.op === '-' ? -toNum(a) : toNum(a);
    }
    case 'bin': {
      const a = evalNode(node.a, ctx);
      const b = evalNode(node.b, ctx);
      const A = Array.isArray(a) ? a[0] : a;
      const B = Array.isArray(b) ? b[0] : b;
      const na = toNum(A), nb = toNum(B);
      switch (node.op) {
        case '+': return na + nb;
        case '-': return na - nb;
        case '*': return na * nb;
        case '/': if (nb === 0) throw new Error(ERR.DIV0); return na / nb;
        case '%': return na % nb;
        case '^': return Math.pow(na, nb);
        default: throw new Error(ERR.VALUE);
      }
    }
    case 'cmp': {
      const a = evalNode(node.a, ctx);
      const b = evalNode(node.b, ctx);
      const A = Array.isArray(a) ? a[0] : a;
      const B = Array.isArray(b) ? b[0] : b;
      // Numeric comparison if both sides are numeric, else string comparison.
      const na = toNum(A), nb = toNum(B);
      const bothNumeric = Number.isFinite(na) && Number.isFinite(nb);
      const [lhs, rhs] = bothNumeric ? [na, nb] : [String(A ?? ''), String(B ?? '')];
      switch (node.op) {
        case '<':  return lhs <  rhs;
        case '<=': return lhs <= rhs;
        case '>':  return lhs >  rhs;
        case '>=': return lhs >= rhs;
        case '=':  return lhs === rhs;
        case '<>': return lhs !== rhs;
        default: throw new Error(ERR.VALUE);
      }
    }
    case 'fn': {
      const flat = (nodes) => {
        const out = [];
        for (const n of nodes) {
          const v = evalNode(n, ctx);
          if (Array.isArray(v)) out.push(...v); else out.push(v);
        }
        return out;
      };
      switch (node.fn) {
        case 'SUM': {
          const vals = flat(node.args).map(toNum).filter(Number.isFinite);
          return vals.reduce((a, b) => a + b, 0);
        }
        case 'AVERAGE': {
          const vals = flat(node.args).map(toNum).filter(Number.isFinite);
          if (!vals.length) throw new Error(ERR.DIV0);
          return vals.reduce((a, b) => a + b, 0) / vals.length;
        }
        case 'MIN': {
          const vals = flat(node.args).map(toNum).filter(Number.isFinite);
          return vals.length ? Math.min(...vals) : 0;
        }
        case 'MAX': {
          const vals = flat(node.args).map(toNum).filter(Number.isFinite);
          return vals.length ? Math.max(...vals) : 0;
        }
        case 'COUNT': {
          return flat(node.args).map(toNum).filter(Number.isFinite).length;
        }
        case 'COUNTA': {
          return flat(node.args).filter(v => v != null && v !== '').length;
        }
        case 'SUMIF': {
          if (node.args.length < 2) throw new Error(ERR.VALUE);
          const range   = evalNode(node.args[0], ctx);
          const crit    = evalNode(node.args[1], ctx);
          const sumRng  = node.args[2] ? evalNode(node.args[2], ctx) : range;
          const r = Array.isArray(range) ? range : [range];
          const s = Array.isArray(sumRng) ? sumRng : [sumRng];
          let total = 0;
          for (let i = 0; i < r.length; i++) {
            if (matchCriteria(r[i], crit)) total += toNum(s[i]) || 0;
          }
          return total;
        }
        case 'COUNTIF': {
          if (node.args.length < 2) throw new Error(ERR.VALUE);
          const range = evalNode(node.args[0], ctx);
          const crit  = evalNode(node.args[1], ctx);
          const r = Array.isArray(range) ? range : [range];
          let n = 0;
          for (const v of r) if (matchCriteria(v, crit)) n++;
          return n;
        }
        case 'AVERAGEIF': {
          if (node.args.length < 2) throw new Error(ERR.VALUE);
          const range   = evalNode(node.args[0], ctx);
          const crit    = evalNode(node.args[1], ctx);
          const avgRng  = node.args[2] ? evalNode(node.args[2], ctx) : range;
          const r = Array.isArray(range) ? range : [range];
          const a = Array.isArray(avgRng) ? avgRng : [avgRng];
          let sum = 0, cnt = 0;
          for (let i = 0; i < r.length; i++) {
            if (matchCriteria(r[i], crit)) { sum += toNum(a[i]) || 0; cnt++; }
          }
          if (!cnt) throw new Error(ERR.DIV0);
          return sum / cnt;
        }
        case 'IF': {
          if (node.args.length < 2) throw new Error(ERR.VALUE);
          const cond = evalNode(node.args[0], ctx);
          const truthy = Array.isArray(cond) ? toBool(cond[0]) : toBool(cond);
          return truthy
            ? evalNode(node.args[1], ctx)
            : (node.args[2] ? evalNode(node.args[2], ctx) : false);
        }
        case 'AND': return flat(node.args).every(toBool);
        case 'OR':  return flat(node.args).some(toBool);
        case 'ROUND': {
          const n = toNum(evalNode(node.args[0], ctx));
          const d = node.args[1] ? toNum(evalNode(node.args[1], ctx)) : 0;
          const p = Math.pow(10, d);
          return Math.round(n * p) / p;
        }
        case 'ROUNDUP': {
          const n = toNum(evalNode(node.args[0], ctx));
          const d = node.args[1] ? toNum(evalNode(node.args[1], ctx)) : 0;
          const p = Math.pow(10, d);
          return (n >= 0 ? Math.ceil(n * p) : Math.floor(n * p)) / p;
        }
        case 'ROUNDDOWN': {
          const n = toNum(evalNode(node.args[0], ctx));
          const d = node.args[1] ? toNum(evalNode(node.args[1], ctx)) : 0;
          const p = Math.pow(10, d);
          return (n >= 0 ? Math.floor(n * p) : Math.ceil(n * p)) / p;
        }
        default: throw new Error(ERR.NAME);
      }
    }
    default: throw new Error(ERR.VALUE);
  }
}

// ── Public entry point ────────────────────────────────────────────────
export function evaluate(formula, cellLookup) {
  if (typeof formula !== 'string') {
    return { value: formula, error: null, kind: typeof formula };
  }
  const s = formula.trim();
  if (!s) return { value: '', error: null, kind: 'string' };
  if (!s.startsWith('=')) {
    // Plain literal — number if parseable else string
    const n = Number(s.replace(/,/g, ''));
    if (Number.isFinite(n)) return { value: n, error: null, kind: 'number' };
    return { value: s, error: null, kind: 'string' };
  }
  try {
    const tokens = tokenize(s.slice(1));
    const parser = new Parser(tokens);
    const ast = parser.parseExpr();
    if (parser.p !== tokens.length) throw new SyntaxError(ERR.VALUE);
    const ctx = { cellLookup, visited: new Set() };
    let value = evalNode(ast, ctx);
    if (Array.isArray(value)) value = value[0];
    if (typeof value === 'number' && !Number.isFinite(value)) {
      return { value: null, error: ERR.NUM, kind: 'error' };
    }
    const kind = typeof value === 'boolean' ? 'boolean'
              : typeof value === 'number'   ? 'number'
              : 'string';
    return { value, error: null, kind };
  } catch (e) {
    const msg = e.message || String(e);
    const known = Object.values(ERR).find(k => msg.includes(k));
    return { value: null, error: known || ERR.VALUE, kind: 'error' };
  }
}

export const FORMULA_ERRORS = ERR;
